import type { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { locateSpan, type LocatableSpan } from './span-locator'

/**
 * Applying a promotion suggestion edits the author's TEXT — it does not add a rule anywhere.
 * That is forced by the compiler's own contract: compiled rules are re-derived from the body on
 * every compile, so a binding introduced any other way would evaporate on the next Analyze.
 *
 * Everything here is therefore about locating the right characters to replace, safely, in a body
 * the author may have kept editing since the compile these suggestions were derived from.
 */

/** Sentence terminators, for avoiding a doubled one when the replacement supplies its own. */
const TERMINATORS = new Set(['.', '!', '?'])

/**
 * The body with `replacement` in place of the author's vague sentence, or null when the span can
 * no longer be located.
 *
 * Two adjustments beyond a raw splice, both to avoid obviously-broken output:
 *   - a sentence terminator immediately after the span is consumed, since the replacement brings
 *     its own ("Keep titles short." would otherwise become "…characters..")
 *   - the first letter is lowercased when the replacement lands mid-sentence, so a clause
 *     replaced inside a longer sentence doesn't grow a capital in the middle of it
 *
 * Deliberately no cleverer than that. The result goes straight into the visible textarea for the
 * author to read and edit before they click Analyze — an unnatural join is something they can
 * see and fix, whereas a rewrite that changed their meaning to read more smoothly is not.
 */
export function applyReplacement(
    body: string,
    replaces: LocatableSpan,
    replacement: string,
    compiledBody?: string,
): string | null {
    const located = locateSpan(body, replaces, compiledBody)
    if (located === null) return null

    let end = located.end
    if (TERMINATORS.has(replacement.slice(-1)) && TERMINATORS.has(body.charAt(end))) end += 1

    const preceding = body.slice(0, located.start)
    const precedingText = preceding.trimEnd()
    // A line break before the span ends the sentence just as surely as a period does, and real
    // directives are full of statements written one per line with no terminating punctuation
    // ("Always spell out numbers one through nine" / newline / "Write short sentences"). Deciding
    // this from `trimEnd()` alone throws away the newline that settles it, and then lowercases
    // the first word of what is plainly its own line.
    const brokeLine = preceding.length > precedingText.length && /[\r\n]/.test(preceding.slice(precedingText.length))
    const midSentence = precedingText !== '' && !brokeLine && !TERMINATORS.has(precedingText.slice(-1))
    const text = midSentence ? replacement.charAt(0).toLowerCase() + replacement.slice(1) : replacement

    return body.slice(0, located.start) + text + body.slice(end)
}

/**
 * Suggestions grouped by the statement they are about, preserving server order.
 *
 * A statement that named a length but no field ("Be punchy") produces one suggestion PER
 * plausible field, all sharing an `item_text`. They are alternatives, not a list of things to do
 * — rendering them as separate suggestions would read as "apply both", which would put two hard
 * limits on the author for one sentence they wrote.
 */
export function groupSuggestionsByItem(
    suggestions: DirectivePromotionSuggestion[],
): Map<string, DirectivePromotionSuggestion[]> {
    const grouped = new Map<string, DirectivePromotionSuggestion[]>()

    for (const suggestion of suggestions) {
        const group = grouped.get(suggestion.item_text)
        if (group) group.push(suggestion)
        else grouped.set(suggestion.item_text, [suggestion])
    }

    return grouped
}

/**
 * The four length dimensions a suggestion can target, and everything the UI needs to name one.
 *
 * Mirrors `AI_DIRECTIVE_BINDINGS_V1` rather than reading it: the catalog holds 13 bindings whose
 * value kinds and attributes are the compiler's business, while this is a small closed set the
 * author is choosing between in a form. Deriving it would mean filtering the catalog by naming
 * conventions in the ids, which is a weaker guarantee than four explicit rows.
 *
 * The unit is part of the identity, not decoration. "Under 62" is meaningless on its own —
 * 62 characters and 62 words are wildly different rules, and the author is being asked to commit
 * to one of them.
 */
export interface PromotionDimension {
    bindingId: string
    attribute: string
    field: 'title' | 'body'
    unit: 'characters' | 'words'
    /** Plural, sentence-leading — "Titles must stay under 62 characters." */
    fieldLabel: string
}

export const PROMOTION_DIMENSIONS: PromotionDimension[] = [
    {
        bindingId: 'title_max_length',
        attribute: 'title.charLength',
        field: 'title',
        unit: 'characters',
        fieldLabel: 'Titles',
    },
    {
        bindingId: 'title_max_words',
        attribute: 'title.wordLength',
        field: 'title',
        unit: 'words',
        fieldLabel: 'Titles',
    },
    {
        bindingId: 'body_max_length',
        attribute: 'body.charLength',
        field: 'body',
        unit: 'characters',
        fieldLabel: 'Bodies',
    },
    { bindingId: 'body_max_words', attribute: 'body.wordLength', field: 'body', unit: 'words', fieldLabel: 'Bodies' },
]

export function dimensionByBindingId(bindingId: string): PromotionDimension | undefined {
    return PROMOTION_DIMENSIONS.find((d) => d.bindingId === bindingId)
}

export function dimensionFor(field: 'title' | 'body', unit: 'characters' | 'words'): PromotionDimension {
    return PROMOTION_DIMENSIONS.find((d) => d.field === field && d.unit === unit)!
}

/**
 * The sentence that goes into the author's text.
 *
 * Built here rather than taken from the server's `suggested_text` because the author can change
 * the field, the unit and the number before confirming — at which point the server's sentence
 * describes a rule they rejected. One builder owning every author-visible wording is the point:
 * two of them, one for the untouched suggestion and one for an adjusted one, is precisely how a
 * preview ends up disagreeing with what actually gets inserted.
 *
 * Kept word-for-word identical to sailfish's own `replacementSentence`, so an untouched
 * suggestion inserts exactly the sentence the server proposed.
 */
export function promotionSentence(dimension: PromotionDimension, value: number): string {
    return `${dimension.fieldLabel} must stay under ${value} ${dimension.unit}.`
}

/**
 * The one-line justification for a limit on `dimension`, or null when there is no measurement
 * behind it.
 *
 * Null is a real answer, not a gap to fill with a plausible sentence: the author may retarget a
 * suggestion onto a dimension the corpus never exercised (a domain that never writes bodies),
 * and inventing a reason for a number nobody measured is worse than admitting there isn't one.
 */
export function promotionRationale(
    dimension: PromotionDimension,
    p90ByAttribute: Record<string, number>,
    p50ByAttribute: Record<string, number>,
    sampleSize: number,
): string | null {
    const p90 = p90ByAttribute?.[dimension.attribute]
    if (!p90) return null

    const p50 = p50ByAttribute?.[dimension.attribute]
    const field = dimension.field === 'title' ? 'titles' : 'bodies'
    const ceiling = `90% are under ${p90} ${dimension.unit} (${sampleSize} notifications)`

    // The median leads. The ceiling explains where the suggested value came from, but on its own
    // it reads as a recommendation — and p90 recommends almost nothing, since by construction it
    // permits nine tenths of everything already sent. An author who wrote "write short sentences"
    // and is offered "under 99 characters" needs to see that 48 is typical before they can judge
    // whether 99 is the rule they meant.
    return p50
        ? `Your ${field} typically run ${p50} ${dimension.unit}, and ${ceiling}.`
        : `Of your ${field} from the last 90 days, ${ceiling}.`
}
